import { SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { t } from "@lingui/macro";
import { DisplayPreferences } from "@WahooFitness/cloud-client-ts";
import {
  DisplayPreferenceInputType,
  PlanRecommendationsPreference,
} from "@WahooFitness/cloud-client-types";
import {
  useCloudContext,
  useConfigContext,
  useSnackbarContext,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useMemo } from "react";

function useWorkoutRecommendations() {
  const { getCloudClient } = useCloudContext();
  const displayPreferences = useMemo(() => getCloudClient(DisplayPreferences), [getCloudClient]);
  const { currentUser, mutateUser } = useUserContext();
  const { wahooToken } = useConfigContext();
  const { enqueueSnackbar } = useSnackbarContext();
  const { logEvent } = useLogEvent();
  const isCyclingToggled = useMemo(
    () =>
      currentUser?.display_preference?.plan_recommendations !== undefined &&
      [
        PlanRecommendationsPreference.when_no_training_plan,
        PlanRecommendationsPreference.always,
        PlanRecommendationsPreference.when_nothing_scheduled,
      ].includes(currentUser.display_preference.plan_recommendations),
    [currentUser]
  );

  const updateDisplayPreferences = useCallback(
    async (preference: PlanRecommendationsPreference) => {
      if (!currentUser) return;
      try {
        const newPreferences = await displayPreferences.update(wahooToken, {
          plan_recommendations: preference,
        } as DisplayPreferenceInputType);
        mutateUser(
          {
            ...currentUser,
            display_preference: newPreferences,
          },
          { revalidate: false }
        );
      } catch {
        enqueueSnackbar({
          message: t`An error occurred when saving recommendations. Please check your internet connection and try again.`,
          severity: "error",
        });
      }
    },
    [currentUser, displayPreferences, enqueueSnackbar, mutateUser, wahooToken]
  );

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      logEvent("workout_recommendation_setting", {
        click_target: event.target.checked ? "ON" : "OFF",
        source_page_name: SourcePage.settings,
      });
      if (event.target.checked) {
        updateDisplayPreferences(PlanRecommendationsPreference.always);
      } else {
        updateDisplayPreferences(PlanRecommendationsPreference.never);
      }
    },
    [logEvent, updateDisplayPreferences]
  );

  return {
    isCyclingToggled,
    handleToggle,
  };
}

export default useWorkoutRecommendations;
